import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  isAfter,
  isBefore,
  startOfDay,
  subDays,
} from 'date-fns'
import { PointerEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { OrderDateRange, parseDateKey, toDateKey } from './dateRangePresets'

const DEFAULT_CELL_WIDTH = 26
const DESKTOP_VISIBLE_DAYS = 36
const EDGE_SCROLL_ZONE = 36
const EDGE_SCROLL_RAMP = 120
const MIN_EDGE_SCROLL_STEP = 0.25
const MAX_EDGE_SCROLL_STEP = 5

type RangeHandle = 'from' | 'to'

export interface TimelineFocusRequest {
  id: number
  date: string
  align: RangeHandle
}

interface DateRangeTimelineProps {
  range: OrderDateRange | null
  onChange: (range: OrderDateRange) => void
  focusRequest: TimelineFocusRequest
}

interface ActiveDrag {
  handle: RangeHandle
  range: OrderDateRange
}

interface ActiveScrollbarDrag {
  pointerId: number
  startClientX: number
  startScrollLeft: number
}

interface TimelineMonthSegment {
  key: string
  month: string
  year: string
  dayCount: number
}

interface PendingReleasePosition {
  date: string
  viewportOffset: number
}

const DateRangeTimeline = ({ range, onChange, focusRequest }: DateRangeTimelineProps) => {
  const today = useMemo(() => startOfDay(new Date()), [])
  const todayKey = toDateKey(today)
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollbarTrackRef = useRef<HTMLDivElement>(null)
  const scrollbarThumbRef = useRef<HTMLButtonElement>(null)
  const activeDragRef = useRef<ActiveDrag | null>(null)
  const activeScrollbarDragRef = useRef<ActiveScrollbarDrag | null>(null)
  const handledFocusRequestRef = useRef<{ id: number; cellWidth: number } | null>(null)
  const dragPointerClientXRef = useRef<number | null>(null)
  const edgeScrollFrameRef = useRef<number | null>(null)
  const updateDragRangeRef = useRef<(clientX: number, autoScroll: boolean) => boolean>(() => false)
  const pendingReleasePositionRef = useRef<PendingReleasePosition | null>(null)
  const [dragTimelineStartKey, setDragTimelineStartKey] = useState<string | null>(null)
  const [cellWidth, setCellWidth] = useState(DEFAULT_CELL_WIDTH)

  const fallbackRange = useMemo<OrderDateRange>(
    () => ({
      from: toDateKey(subDays(today, 29)),
      to: todayKey,
    }),
    [today, todayKey]
  )

  const displayRange = range || fallbackRange
  const displayFrom = useMemo(() => parseDateKey(displayRange.from) || today, [displayRange.from, today])
  const displayTo = useMemo(() => parseDateKey(displayRange.to) || today, [displayRange.to, today])
  const naturalTimelineStart = useMemo(() => {
    const selectionStartWithContext = subDays(displayFrom, 3)
    const thirtyDayWindowStart = subDays(today, 32)

    return isBefore(selectionStartWithContext, thirtyDayWindowStart) ? selectionStartWithContext : thirtyDayWindowStart
  }, [displayFrom, today])
  const dragTimelineStart = dragTimelineStartKey ? parseDateKey(dragTimelineStartKey) : null
  const earliestDate =
    dragTimelineStart && isBefore(dragTimelineStart, naturalTimelineStart) ? dragTimelineStart : naturalTimelineStart
  const timelineEnd = useMemo(() => addDays(today, 3), [today])
  const dates = useMemo(() => eachDayOfInterval({ start: earliestDate, end: timelineEnd }), [earliestDate, timelineEnd])
  const monthSegments = useMemo(
    () =>
      dates.reduce<TimelineMonthSegment[]>((segments, date) => {
        const monthKey = format(date, 'yyyy-MM')
        const currentSegment = segments[segments.length - 1]

        if (currentSegment?.key === monthKey) {
          currentSegment.dayCount += 1
        } else {
          segments.push({
            key: monthKey,
            month: format(date, 'MMM'),
            year: format(date, 'yyyy'),
            dayCount: 1,
          })
        }

        return segments
      }, []),
    [dates]
  )
  const todayIndex = differenceInCalendarDays(today, earliestDate)
  const fromIndex = Math.max(0, differenceInCalendarDays(displayFrom, earliestDate))
  const toIndex = Math.min(dates.length - 1, differenceInCalendarDays(displayTo, earliestDate))
  const selectedFrom = range ? parseDateKey(range.from) : null
  const selectedTo = range ? parseDateKey(range.to) : null

  useEffect(() => {
    if (!activeDragRef.current) return

    setDragTimelineStartKey((currentKey) => {
      const currentStart = currentKey ? parseDateKey(currentKey) : null

      return !currentStart || isBefore(naturalTimelineStart, currentStart)
        ? toDateKey(naturalTimelineStart)
        : currentKey
    })
  }, [naturalTimelineStart])

  useEffect(() => {
    const scrollContainer = scrollRef.current
    if (!scrollContainer) return

    const updateCellWidth = () => {
      const nextCellWidth = scrollContainer.clientWidth / DESKTOP_VISIBLE_DAYS

      if (nextCellWidth > 0) {
        setCellWidth((currentWidth) => (Math.abs(currentWidth - nextCellWidth) > 0.01 ? nextCellWidth : currentWidth))
      }
    }

    const resizeObserver = new ResizeObserver(updateCellWidth)
    resizeObserver.observe(scrollContainer)
    updateCellWidth()

    return () => resizeObserver.disconnect()
  }, [])

  const updateCustomScrollbar = useCallback(() => {
    const scrollContainer = scrollRef.current
    const scrollbarTrack = scrollbarTrackRef.current
    const scrollbarThumb = scrollbarThumbRef.current
    if (!scrollContainer || !scrollbarTrack || !scrollbarThumb) return

    const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth
    const minimumThumbWidth = 40
    const proportionalWidth = (scrollContainer.clientWidth / scrollContainer.scrollWidth) * scrollbarTrack.clientWidth
    const thumbWidth = Math.max(minimumThumbWidth, Math.min(scrollbarTrack.clientWidth, proportionalWidth))
    const maxThumbLeft = scrollbarTrack.clientWidth - thumbWidth
    const thumbLeft = maxScrollLeft > 0 ? (scrollContainer.scrollLeft / maxScrollLeft) * maxThumbLeft : 0
    const startToTodayDayCount = Math.abs(todayIndex - fromIndex) + 1
    const contextDayCount = 6
    const startToTodayWidth = (startToTodayDayCount + contextDayCount) * cellWidth
    const doesStartToTodayFit = startToTodayWidth <= scrollContainer.clientWidth

    scrollbarThumb.style.width = `${thumbWidth}px`
    scrollbarThumb.style.transform = `translateX(${thumbLeft}px)`
    scrollbarTrack.style.opacity = doesStartToTodayFit ? '0' : '1'
    scrollbarTrack.style.visibility = doesStartToTodayFit ? 'hidden' : 'visible'
    scrollbarTrack.style.pointerEvents = doesStartToTodayFit ? 'none' : 'auto'
  }, [cellWidth, fromIndex, todayIndex])

  useLayoutEffect(() => {
    const pendingPosition = pendingReleasePositionRef.current
    const scrollContainer = scrollRef.current
    if (dragTimelineStartKey !== null || !pendingPosition || !scrollContainer) return

    const releasedDate = parseDateKey(pendingPosition.date)
    if (!releasedDate) {
      pendingReleasePositionRef.current = null
      return
    }

    const releasedDateIndex = differenceInCalendarDays(releasedDate, earliestDate)
    const releasedHandleCenter = releasedDateIndex * cellWidth + cellWidth / 2
    const maxScrollLeft = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth)
    const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, releasedHandleCenter - pendingPosition.viewportOffset))

    scrollContainer.scrollLeft = nextScrollLeft
    pendingReleasePositionRef.current = null
    updateCustomScrollbar()
  }, [cellWidth, dragTimelineStartKey, earliestDate, updateCustomScrollbar])

  useEffect(() => {
    const scrollContainer = scrollRef.current
    const scrollbarTrack = scrollbarTrackRef.current
    if (!scrollContainer || !scrollbarTrack) return

    const resizeObserver = new ResizeObserver(updateCustomScrollbar)
    resizeObserver.observe(scrollContainer)
    resizeObserver.observe(scrollbarTrack)
    updateCustomScrollbar()

    return () => resizeObserver.disconnect()
  }, [dates.length, updateCustomScrollbar])

  useEffect(() => {
    const scrollContainer = scrollRef.current
    const focusDate = parseDateKey(focusRequest.date)
    if (!scrollContainer || !focusDate) return
    const handledFocusRequest = handledFocusRequestRef.current
    if (handledFocusRequest?.id === focusRequest.id && Math.abs(handledFocusRequest.cellWidth - cellWidth) < 0.01) {
      return
    }

    handledFocusRequestRef.current = { id: focusRequest.id, cellWidth }

    const dateIndex = differenceInCalendarDays(focusDate, earliestDate)
    const visibleDays = Math.max(1, Math.round(scrollContainer.clientWidth / cellWidth))
    const contextDays = 3
    const targetIndex =
      focusRequest.align === 'from' ? dateIndex - contextDays : dateIndex - visibleDays + contextDays + 1

    scrollContainer.scrollTo({
      left: Math.max(0, targetIndex * cellWidth),
      behavior: 'smooth',
    })
  }, [cellWidth, earliestDate, focusRequest, updateCustomScrollbar])

  const getDateFromClientX = (clientX: number) => {
    const content = contentRef.current
    if (!content) return today

    const contentRect = content.getBoundingClientRect()
    const rawIndex = Math.round((clientX - contentRect.left - cellWidth / 2) / cellWidth)
    const clampedIndex = Math.max(0, Math.min(todayIndex, rawIndex))
    return dates[clampedIndex]
  }

  const updateDragRange = (clientX: number, autoScroll: boolean) => {
    const activeDrag = activeDragRef.current
    if (!activeDrag) return false

    let constrainedClientX = clientX
    let shouldContinueAutoScroll = false
    const scrollContainer = scrollRef.current
    if (scrollContainer) {
      const scrollRect = scrollContainer.getBoundingClientRect()
      const handleContext = Math.max(EDGE_SCROLL_ZONE, cellWidth * 3)
      const minimumHandleClientX = scrollRect.left + handleContext
      const maximumHandleClientX = scrollRect.right - handleContext
      const distanceFromLeftEdge = minimumHandleClientX - clientX
      const distanceFromRightEdge = clientX - maximumHandleClientX

      constrainedClientX = Math.max(minimumHandleClientX, Math.min(maximumHandleClientX, clientX))

      if (autoScroll && (distanceFromLeftEdge > 0 || distanceFromRightEdge > 0)) {
        const scrollDirection = distanceFromLeftEdge > 0 ? -1 : 1
        const outsideDistance = Math.max(distanceFromLeftEdge, distanceFromRightEdge)
        const intensity = Math.min(1, outsideDistance / EDGE_SCROLL_RAMP)
        const scrollStep = MIN_EDGE_SCROLL_STEP + intensity * (MAX_EDGE_SCROLL_STEP - MIN_EDGE_SCROLL_STEP)
        const previousScrollLeft = scrollContainer.scrollLeft

        scrollContainer.scrollLeft += scrollDirection * scrollStep
        shouldContinueAutoScroll = Math.abs(scrollContainer.scrollLeft - previousScrollLeft) > 0.01
      }
    }

    const nextDate = toDateKey(getDateFromClientX(constrainedClientX))
    const nextRange = { ...activeDrag.range }

    if (activeDrag.handle === 'from') {
      nextRange.from = nextDate > nextRange.to ? nextRange.to : nextDate
    } else {
      nextRange.to = nextDate < nextRange.from ? nextRange.from : nextDate
    }

    if (nextRange.from !== activeDrag.range.from || nextRange.to !== activeDrag.range.to) {
      activeDragRef.current = { ...activeDrag, range: nextRange }
      onChange(nextRange)
    }

    return shouldContinueAutoScroll
  }

  updateDragRangeRef.current = updateDragRange

  const startEdgeAutoScroll = () => {
    if (edgeScrollFrameRef.current !== null) return

    const scrollFrame = () => {
      edgeScrollFrameRef.current = null

      const clientX = dragPointerClientXRef.current
      if (clientX === null || !activeDragRef.current) return

      if (updateDragRangeRef.current(clientX, true)) {
        edgeScrollFrameRef.current = requestAnimationFrame(scrollFrame)
      }
    }

    edgeScrollFrameRef.current = requestAnimationFrame(scrollFrame)
  }

  const stopEdgeAutoScroll = () => {
    dragPointerClientXRef.current = null

    if (edgeScrollFrameRef.current !== null) {
      cancelAnimationFrame(edgeScrollFrameRef.current)
      edgeScrollFrameRef.current = null
    }
  }

  const updateRangeFromPointer = (event: PointerEvent<HTMLButtonElement>) => {
    dragPointerClientXRef.current = event.clientX
    updateDragRangeRef.current(event.clientX, false)
    startEdgeAutoScroll()
  }

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>, handle: RangeHandle) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    activeDragRef.current = { handle, range: range || fallbackRange }
    dragPointerClientXRef.current = event.clientX
    setDragTimelineStartKey(toDateKey(naturalTimelineStart))
    startEdgeAutoScroll()

    if (!range) onChange(fallbackRange)
  }

  const handlePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    const activeDrag = activeDragRef.current
    const scrollContainer = scrollRef.current

    if (activeDrag && scrollContainer) {
      const handleRect = event.currentTarget.getBoundingClientRect()
      const scrollRect = scrollContainer.getBoundingClientRect()

      pendingReleasePositionRef.current = {
        date: activeDrag.range[activeDrag.handle],
        viewportOffset: handleRect.left + handleRect.width / 2 - scrollRect.left,
      }
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    activeDragRef.current = null
    stopEdgeAutoScroll()
    setDragTimelineStartKey(null)
  }

  useEffect(
    () => () => {
      if (edgeScrollFrameRef.current !== null) {
        cancelAnimationFrame(edgeScrollFrameRef.current)
      }
    },
    []
  )

  const handleScrollbarTrackPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return

    const scrollContainer = scrollRef.current
    const scrollbarTrack = scrollbarTrackRef.current
    const scrollbarThumb = scrollbarThumbRef.current
    if (!scrollContainer || !scrollbarTrack || !scrollbarThumb) return

    const trackRect = scrollbarTrack.getBoundingClientRect()
    const targetThumbLeft = event.clientX - trackRect.left - scrollbarThumb.clientWidth / 2
    const maxThumbLeft = scrollbarTrack.clientWidth - scrollbarThumb.clientWidth
    const thumbRatio = maxThumbLeft > 0 ? Math.max(0, Math.min(maxThumbLeft, targetThumbLeft)) / maxThumbLeft : 0

    scrollContainer.scrollTo({
      left: thumbRatio * (scrollContainer.scrollWidth - scrollContainer.clientWidth),
      behavior: 'smooth',
    })
  }

  const handleScrollbarThumbPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.currentTarget.focus()
    event.currentTarget.setPointerCapture(event.pointerId)
    activeScrollbarDragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: scrollRef.current?.scrollLeft || 0,
    }
  }

  const handleScrollbarThumbPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const activeDrag = activeScrollbarDragRef.current
    const scrollContainer = scrollRef.current
    const scrollbarTrack = scrollbarTrackRef.current
    const scrollbarThumb = scrollbarThumbRef.current
    if (!activeDrag || !scrollContainer || !scrollbarTrack || !scrollbarThumb) return

    const maxThumbLeft = scrollbarTrack.clientWidth - scrollbarThumb.clientWidth
    const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth
    const scrollRatio = maxThumbLeft > 0 ? maxScrollLeft / maxThumbLeft : 0

    scrollContainer.scrollLeft = activeDrag.startScrollLeft + (event.clientX - activeDrag.startClientX) * scrollRatio
  }

  const handleScrollbarThumbPointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    activeScrollbarDragRef.current = null
    event.currentTarget.blur()
  }

  return (
    <div className="rounded-md bg-gray-100 pt-md">
      <style>{`
                .order-date-timeline-scroll {
                    scrollbar-width: none;
                }
                .order-date-timeline-scroll::-webkit-scrollbar {
                    display: none;
                }
            `}</style>

      <div
        ref={scrollRef}
        className="order-date-timeline-scroll w-full overflow-x-scroll overscroll-x-contain"
        onScroll={updateCustomScrollbar}
      >
        <div ref={contentRef} className="relative h-[99px] select-none" style={{ width: dates.length * cellWidth }}>
          <div className="absolute left-0 top-0 flex h-[24px] w-full">
            {monthSegments.map((month) => (
              <div
                key={month.key}
                className="relative h-[24px] shrink-0 text-[10px] font-semibold text-gray-500"
                style={{ width: month.dayCount * cellWidth }}
              >
                <span className="sticky left-[9px] inline-flex h-[24px] items-center gap-xs whitespace-nowrap bg-gray-100 pr-xs">
                  <span>{month.month}</span>
                  <span className="h-px w-[8px] bg-gray-300" aria-hidden="true" />
                  <span>{month.year}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="absolute left-0 top-[29px] flex h-[50px]">
            {dates.map((date, dateIndex) => {
              const dateKey = toDateKey(date)
              const isFuture = isAfter(date, today)
              const isWeekend = date.getDay() === 0 || date.getDay() === 6
              const isMonday = date.getDay() === 1
              const isSelected = Boolean(
                selectedFrom && selectedTo && !isBefore(date, selectedFrom) && !isAfter(date, selectedTo)
              )

              return (
                <div
                  key={dateKey}
                  className={`relative flex h-[50px] shrink-0 flex-col items-center justify-center text-center ${
                    isSelected
                      ? 'bg-white text-gray-900'
                      : isFuture
                      ? 'bg-gray-200 text-gray-400'
                      : isWeekend
                      ? 'bg-gray-200 text-primary-600'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  style={{ width: cellWidth }}
                  title={format(date, 'EEEE, MMMM d, yyyy')}
                >
                  {dateIndex > 0 && (
                    <span
                      className={`pointer-events-none absolute left-0 w-px ${
                        isSelected ? 'bg-gray-300' : 'bg-gray-100'
                      } ${isMonday ? 'inset-y-0' : 'bottom-[10px] top-[10px]'}`}
                      aria-hidden="true"
                    />
                  )}
                  <span className="text-[9px] uppercase leading-none">{format(date, 'EEEEE')}</span>
                  <span className="mt-[3px] text-[11px] font-semibold leading-none">{format(date, 'd')}</span>
                </div>
              )
            })}
          </div>

          {(['from', 'to'] as RangeHandle[]).map((handle) => {
            const handleIndex = handle === 'from' ? fromIndex : toIndex
            const handleDate = handle === 'from' ? displayFrom : displayTo

            return (
              <button
                key={handle}
                type="button"
                aria-label={`Drag ${handle} date`}
                className={`absolute top-[24px] flex h-[70px] -translate-x-1/2 touch-none cursor-ew-resize flex-col items-center rounded-sm border outline-none shadow-sm transition-[left,background-color,border-color] duration-100 ease-out ${
                  handle === 'from' ? 'z-[4]' : 'z-[3]'
                } ${
                  range
                    ? 'border-primary-700 bg-primary-500 text-white hover:bg-primary-600 focus:shadow-focus'
                    : 'border-gray-400 bg-gray-300 text-white'
                }`}
                style={{
                  left: handleIndex * cellWidth + cellWidth / 2,
                  width: cellWidth,
                }}
                onPointerDown={(event) => handlePointerDown(event, handle)}
                onPointerMove={updateRangeFromPointer}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                <span
                  className="pointer-events-none absolute left-0 top-[5px] w-full border-t border-white/30"
                  aria-hidden="true"
                />
                <span className="flex h-[55px] w-full flex-col items-center justify-center text-shadow-sm">
                  <span className="text-[9px] uppercase leading-none">{format(handleDate, 'EEEEE')}</span>
                  <span className="mt-[3px] text-[11px] font-semibold leading-none">{format(handleDate, 'd')}</span>
                </span>
                <span className="h-[15px] w-full border-t border-white/30" aria-hidden="true">
                  <span className="mx-auto mt-[5px] block h-[2px] w-[8px] rounded-full bg-white/80 shadow-sm" />
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <div
        ref={scrollbarTrackRef}
        className="invisible relative mt-md h-[22px] w-full touch-none cursor-pointer rounded-b-md rounded-t-none bg-gray-100 opacity-0 transition-opacity"
        onPointerDown={handleScrollbarTrackPointerDown}
      >
        <button
          ref={scrollbarThumbRef}
          type="button"
          aria-label="Scroll timeline"
          className="absolute left-0 top-0 flex h-[22px] touch-none cursor-ew-resize items-center justify-center rounded-md border border-gray-300 bg-white transition-colors focus:border-primary-500 focus:outline-none active:border-primary-500"
          onPointerDown={handleScrollbarThumbPointerDown}
          onPointerMove={handleScrollbarThumbPointerMove}
          onPointerUp={handleScrollbarThumbPointerUp}
          onPointerCancel={handleScrollbarThumbPointerUp}
        >
          <span className="pointer-events-none grid grid-cols-2 gap-[2px]" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, dotIndex) => (
              <span key={dotIndex} className="h-[2px] w-[2px] rounded-full bg-gray-400" />
            ))}
          </span>
        </button>
      </div>
    </div>
  )
}

export default DateRangeTimeline
